import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { AlertPosition, AlertType, DimensionsType, ServiceType, usesWorkDuration } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { UpdateWorkReq } from '../../shared/dto/ReqDtoRepository';
import { hasValidMainSizeUnitForUpdate, normalizeWorkSizesForUpdate } from '../../shared/dto/EntityRepository';
import { useImage } from '../../shared/hooks/useApi/useImage';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import { useSettingStore } from '../../shared/store/settingStore';
import { shouldShowWorkArtist, toUpdateWorkArtistReq } from '../../shared/util/workArtist';
import { useValidation } from '../../shared/hooks/useValidation';
import { useWorkIndexing } from '../../shared/hooks/useWorkIndexing';

const WorkOverviewEditActions: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { isIndexingMode } = useWorkIndexing();
  const { updateWork, deleteWork } = useWorkListActions();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const hasChanged = useWorkViewStore((s) => s.hasChanged);
  const imageChanged = useWorkViewStore((s) => s.imageChanged);
  const pendingImage = useWorkViewStore((s) => s.pendingImage);
  const hasMultiSelected = useWorkViewStore((s) => s.multiSelectedWork.length > 0);
  const { enqueueAlert } = useStandardAlertStore();
  const { uploadImage } = useImage();
  const { isValidVideoUrl } = useValidation();
  const setting = useSettingStore((state) => state.setting);

  if (!isEditMode || isIndexingMode || !activeWork || hasMultiSelected) return null;

  const handleConfirm = async () => {
    const isVideo = usesWorkDuration(activeWork.workType);
    if (isVideo && activeWork.videoUrl && !isValidVideoUrl(activeWork.videoUrl)) return;
    const { uploadFile, artist, ...workWithoutUploadFile } = activeWork;
    if (!hasValidMainSizeUnitForUpdate(activeWork.sizes, activeWork.mainSizeUnit)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SET_MAIN_SIZE_UNIT_REQUIRES_SIZE'),
      });
      return;
    }
    const normalizedSize = normalizeWorkSizesForUpdate(activeWork.sizes, activeWork.mainSizeUnit);
    const baseRequest: UpdateWorkReq = {
      ...workWithoutUploadFile,
      ...normalizedSize,
      updateUploadFileReq: {
        originUrl: uploadFile.originUrl,
        uploadFileId: uploadFile.id,
        allowDownload: uploadFile.allowDownload,
        width: pendingImage?.width ?? uploadFile.width,
        height: pendingImage?.height ?? uploadFile.height,
      },
      tagIds: activeWork.tags.map((t) => t.id),
      creditId: activeWork.credit?.id ?? null,
      ...(shouldShowWorkArtist(setting)
        ? { artist: toUpdateWorkArtistReq(artist) }
        : {}),
      ...(isVideo
        ? { dimensionsType: DimensionsType.NONE }
        : { videoUrl: '', duration: '' }),
    };

    const finalRequest = imageChanged
      ? await uploadImage(ServiceType.WORK, baseRequest, pendingImage?.file)
      : baseRequest;
    if (!finalRequest) return;

    await updateWork(finalRequest as UpdateWorkReq);
  };

  const handleDelete = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_WORK'),
      callBack: async () => {
        await deleteWork({ workId: activeWork.id });
      },
    });
  };

  return (
    <Actions
      data-has-changed={hasChanged || imageChanged ? 'true' : 'false'}
    >
      {hasChanged && (
        <UniversalIconBtn
          isPrimary={true}
          size="fit"
          border={true}
          backgroundImg={confirmIcon}
          backgroundImgHover={confirmIcon2}
          onClick={handleConfirm}
        />
      )}
      <UniversalIconBtn
        size="fit"
        border={true}
        backgroundImg={deleteCloseIcon}
        backgroundImgHover={deleteOpenIcon}
        onClick={handleDelete}
      />
    </Actions>
  );
};

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
  margin-left: auto;
`;

export default WorkOverviewEditActions;
